import React, { useEffect, useState } from "react"

import { LanguageIcon, SpeakerWaveIcon } from "@heroicons/react/24/outline"

import { VocabularyWord } from "./useVocabularyWords"

type StudentVocabularyPanelProps = {
  classPeriodId?: string
  latestWordId?: string
  translationLanguageCode?: string | null
  words: VocabularyWord[]
}

const StudentVocabularyPanel: React.FC<StudentVocabularyPanelProps> = ({
  classPeriodId,
  latestWordId,
  translationLanguageCode,
  words,
}) => {
  const [expandedWordIds, setExpandedWordIds] = useState<string[]>([])

  useEffect(() => {
    setExpandedWordIds([])
  }, [classPeriodId])

  useEffect(() => {
    if (latestWordId) setExpandedWordIds([latestWordId])
  }, [latestWordId])

  const toggleWord = (wordId: string) => {
    setExpandedWordIds(currentIds =>
      currentIds.includes(wordId)
        ? currentIds.filter(id => id !== wordId)
        : [...currentIds, wordId]
    )
  }

  const speakWord = (word: string, languageCode?: string) => {
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(word)
    utterance.lang = languageCode || "en-US"
    window.speechSynthesis.speak(utterance)
  }

  return (
    <section className="m-0 flex h-full min-h-0 flex-col bg-neutral-900 text-white">
      <header className="flex-none border-t-0 border-l-0 border-r-0 border-b border-solid border-neutral-700 px-4 py-3 font-semibold">
        Vocabulary
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {words.length === 0 ? (
          <p className="p-4 text-sm text-neutral-400">
            Vocabulary words will appear here.
          </p>
        ) : (
          <ul className="m-0 list-none p-0">
            {words.map(word => {
              const expanded = expandedWordIds.includes(word.id)
              const translatedWord = word.translations.find(
                translation => translation.code === translationLanguageCode
              )?.words[0]?.word

              return (
                <li
                  key={word.id}
                  className="border-t-0 border-l-0 border-r-0 border-b border-solid border-neutral-700"
                >
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => toggleWord(word.id)}
                      className="min-w-0 flex-1 appearance-none border-0 bg-transparent px-4 py-3 text-left text-xl font-semibold"
                      aria-expanded={expanded}
                    >
                      <span className="text-sky-300">
                        {word.word}
                        {word.partOfSpeech && (
                          <span className="ml-2 text-sm font-normal italic text-neutral-400">
                            {word.partOfSpeech}
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => speakWord(word.word)}
                      className="mr-3 flex appearance-none items-center rounded border-0 bg-transparent p-2 text-sky-300 hover:bg-neutral-800 hover:text-sky-200"
                      aria-label={`Hear ${word.word}`}
                    >
                      <SpeakerWaveIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    {translatedWord && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            speakWord(translatedWord, translationLanguageCode)
                          }
                          className="mr-3 flex appearance-none items-center rounded border-0 bg-transparent p-2 text-sky-300 hover:bg-neutral-800 hover:text-sky-200"
                          aria-label={`Hear ${translatedWord}`}
                        >
                          <LanguageIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                          <SpeakerWaveIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </button>
                      </>
                    )}
                  </div>
                  {expanded && (
                    <p className="mt-0 px-4 pb-1 text-sm">{word.definition}</p>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}

export default StudentVocabularyPanel
